import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const initialState = {
  conversations: [],
  conversationCount: 0,
  page: 0,
  socketUpdateCount: 0,

  tags: [],
  owners: [],
  channels: [
    { _id: 'instagram', name: 'Instagram' },
    { _id: 'facebook', name: 'Facebook' },
  ],

  selectedTags: [],
  selectedOwners: [],
  selectedChannels: [],
  searchText: '',
  selectedConversationStatus: { value: 'open', label: 'Open' },
  selectedSortOrder: 'desc',

  contactSidebarShow: true,

  focusedIndex: 0,

  // Multi-select functionality
  selectedConversations: [],
  isMultiSelectMode: false,

  /*
  ConversationListUpdateStatusCode:
  - 0: New Conversation
  - 1: Update Existing Conversation List
  - 2: Side effect of ConversationListUpdateStatusCode - 1 are completed
  */
  conversationListUpdateStatusCode: 0,
};

const useInboxStore = create(
  devtools(
    (set) => ({
      ...initialState,

      // conversations
      addLinkedinChannel: () =>
        set(
          (state) => ({
            channels: [
              ...state.channels,
              { _id: 'linkedin', name: 'LinkedIn' },
            ],
          }),
          false,
          'addLinkedinChannel'
        ),
      addSmsChannel: () =>
        set(
          (state) => ({
            channels: [...state.channels, { _id: 'sms', name: 'SMS' }],
          }),
          false,
          'addSmsChannel'
        ),
      addEmailChannel: () =>
        set(
          (state) => ({
            channels: [...state.channels, { _id: 'email', name: 'Email' }],
          }),
          false,
          'addEmailChannel'
        ),
      setConversations: (conversations, page, count) =>
        set(
          {
            conversations,
            page,
            conversationCount: count,
          },
          false,
          'setConversations'
        ),
      setNextConversations: (conversations, page, count) =>
        set(
          (state) => ({
            conversations: [...state.conversations, ...conversations],
            page: page,
            count: count,
          }),
          false,
          'setNextConversations'
        ),
      removeConversation: (contactId) =>
        set(
          (state) => ({
            conversations: state.conversations.filter(
              (contact) => contact._id !== contactId
            ),
          }),
          false,
          'removeConversation'
        ),
      removeBulkConversation: (conversationIds) =>
        set(
          (state) => ({
            conversations: state.conversations.filter(
              (conversation) => !conversationIds.includes(conversation._id)
            ),
          }),
          false,
          'removeBulkConversation'
        ),
      updateConversationMessageSocket: (contactData) =>
        set(
          (state) => {
            // Find the index of the conversation
            const index = state.conversations.findIndex(
              (item) => item._id === contactData._id
            );
            console.log(
              'index -> ',
              index,
              contactData.latest_message_text,
              contactData.latest_message_timestamp
            );

            if (index === -1) {
              // If conversation not found, push the conversation to the top
              return {
                ...state,
                conversations: [contactData, ...state.conversations],
              };
            } else {
              // If conversation found, Update the conversation
              const updatedConversations = [
                {
                  ...state.conversations[index],
                  latest_message_text: contactData.latest_message_text,
                  is_latest_message_deleted:
                    contactData.is_latest_message_deleted,
                  latest_message_timestamp:
                    contactData.latest_message_timestamp,
                },
                ...state.conversations.slice(0, index),
                ...state.conversations.slice(index + 1),
              ];

              // Return the new state
              return { ...state, conversations: updatedConversations };
            }
          },
          false,
          'updateConversationMessageSocket'
        ),
      updateConversationMessageDeleteSocket: (contactData) =>
        set(
          (state) => {
            // Find the index of the conversation
            const index = state.conversations.findIndex(
              (item) => item._id === contactData._id
            );
            console.log(
              'message delete - index -> ',
              index,
              contactData.latest_message_text,
              contactData.latest_message_timestamp
            );

            if (index === -1) {
              // If conversation not found, just return current state
              return state;
            } else {
              // If conversation found, Update the conversation with deleted message info
              const updatedConversations = [
                {
                  ...state.conversations[index],
                  is_latest_message_deleted: true,
                  latest_message_timestamp:
                    contactData.latest_message_timestamp,
                },
                ...state.conversations.slice(0, index),
                ...state.conversations.slice(index + 1),
              ];

              // Return the new state
              return { ...state, conversations: updatedConversations };
            }
          },
          false,
          'updateConversationMessageDeleteSocket'
        ),
      updateConversationNewMessage: (id, message, timestamp) =>
        set(
          (state) => {
            // Find the index of the conversation
            const index = state.conversations.findIndex(
              (item) => item._id === id
            );

            if (index === -1) {
              // If conversation not found, just return
              return { state };
            } else {
              // If conversation found, Update the conversation
              const updatedConversations = [
                {
                  ...state.conversations[index],
                  latest_message_text: message,
                  latest_message_timestamp: timestamp,
                },
                ...state.conversations.slice(0, index),
                ...state.conversations.slice(index + 1),
              ];

              // Return the new state
              return { ...state, conversations: updatedConversations };
            }
          },
          false,
          'updateConversationNewMessage'
        ),
      addConversationToTop: (contactData) =>
        set(
          (state) => {
            return {
              ...state,
              conversations: [contactData, ...state.conversations],
            };
          },
          false,
          'addConversationToTop'
        ),
      increaseConversationCount: () =>
        set(
          (state) => ({ conversationCount: state.conversationCount + 1 }),
          false,
          'increaseConversationCount'
        ),
      decreaseConversationCount: () =>
        set(
          (state) => ({ conversationCount: state.conversationCount - 1 }),
          false,
          'decreaseConversationCount'
        ),
      decreaseBulkConversationCount: (count) =>
        set(
          (state) => ({
            conversationCount: state.conversationCount - count,
          }),
          false,
          'decreaseBulkConversationCount'
        ),
      incrementSocketUpdateCount: () =>
        set(
          (state) => ({ socketUpdateCount: state.socketUpdateCount + 1 }),
          false,
          'incrementSocketUpdateCount'
        ),
      resetSocketCount: () =>
        set({ socketUpdateCount: 0 }, false, 'resetSocketCount'),

      // filters settings
      setTags: (tags) =>
        set(
          (state) => {
            const otherTagConditions = [
              { _id: 'No-Tag-SBCCRM', name: 'No Tags' },
              { _id: 'Seen-Not-Responded', name: 'Seen & Not Responded' },
              { _id: 'Not-Responded', name: 'Not Responded' },
            ];

            return {
              ...state,
              tags: [...tags, ...otherTagConditions],
            };
          },
          false,
          'setTags'
        ),
      addTags: (newTag) =>
        set((state) => ({ tags: [...state.tags, newTag] }), false, 'addTags'),
      updateTag: (updatedTag) =>
        set(
          (state) => ({
            tags: state.tags.map((tag) =>
              tag._id === updatedTag._id ? updatedTag : tag
            ),
          }),
          false,
          'updateTag'
        ),
      deleteTag: (tagId) =>
        set(
          (state) => ({
            tags: state.tags.filter((tag) => tag._id !== tagId),
          }),
          false,
          'deleteTag'
        ),

      setOwners: (owners) => set({ owners }, false, 'setOwners'),
      addOwner: (owner) =>
        set(
          (state) => ({ owners: [...state.owners, owner] }),
          false,
          'addOwner'
        ),
      setOwnerRoleToDeleted: (ownerId) =>
        set(
          (state) => {
            const updatedOwners = state.owners.map((owner) => {
              if (owner._id === ownerId) {
                return { ...owner, role: 'deleted' };
              }
              return owner;
            });

            return { owners: updatedOwners };
          },
          false,
          'setOwnerRoleToDeleted'
        ),
      updateOwnerRole: (ownerId, role) =>
        set(
          (state) => {
            const updatedOwners = state.owners.map((owner) =>
              owner._id === ownerId ? { ...owner, role } : owner
            );
            return { owners: updatedOwners };
          },
          false,
          'updateOwnerRole'
        ),
      updateOwnerCalendarUrl: (ownerId, calendarUrl) =>
        set(
          (state) => {
            const updatedOwners = state.owners.map((owner) =>
              owner._id === ownerId
                ? { ...owner, calendar_url: calendarUrl }
                : owner
            );
            return { owners: updatedOwners };
          },
          false,
          'updateOwnerCalendarUrl'
        ),

      setSelectedTags: (tags) =>
        set(
          {
            selectedTags: tags,
          },
          false,
          'setSelectedTags'
        ),
      setSelectedOwners: (owners) =>
        set(
          {
            selectedOwners: owners,
          },
          false,
          'setSelectedOwners'
        ),
      setSelectedChannels: (channels) =>
        set(
          {
            selectedChannels: channels,
          },
          false,
          'setSelectedChannels'
        ),
      setConversationStatus: (status) =>
        set(
          {
            selectedConversationStatus: status,
          },
          false,
          'setConversationStatus'
        ),
      setBulkContactStatus: (conversationIds, status) =>
        set(
          (state) => ({
            conversations: state.conversations.map((conversation) =>
              conversationIds.includes(conversation._id)
                ? { ...conversation, status }
                : conversation
            ),
          }),
          false,
          'setBulkContactStatus'
        ),
      setSearchText: (searchText) =>
        set(
          {
            searchText,
          },
          false,
          'setSearchText'
        ),
      setSelectedSortOrder: (sortOrder) =>
        set(
          {
            selectedSortOrder: sortOrder,
          },
          false,
          'setSelectedSortOrder'
        ),

      setContactSidebarShow: () =>
        set(
          (state) => ({ contactSidebarShow: !state.contactSidebarShow }),
          false,
          'setContactSidebarShow'
        ),

      setFocusedIndex: (index) =>
        set({ focusedIndex: index }, false, 'setFocusedIndex'),

      setConversationListUpdateStatusCode: (code) =>
        set(
          { conversationListUpdateStatusCode: code },
          false,
          'setConversationListUpdateStatusCode'
        ),
      resetInboxStore: () => set(initialState, false, 'resetInboxStore'),

      // Multi-select functionality
      toggleMultiSelectMode: () =>
        set(
          (state) => ({
            isMultiSelectMode: !state.isMultiSelectMode,
            selectedConversations: state.isMultiSelectMode
              ? []
              : state.selectedConversations,
          }),
          false,
          'toggleMultiSelectMode'
        ),
      toggleConversationSelection: (conversationId) =>
        set(
          (state) => {
            const isSelected =
              state.selectedConversations.includes(conversationId);
            if (isSelected) {
              return {
                selectedConversations: state.selectedConversations.filter(
                  (id) => id !== conversationId
                ),
              };
            } else {
              return {
                selectedConversations: [
                  ...state.selectedConversations,
                  conversationId,
                ],
              };
            }
          },
          false,
          'toggleConversationSelection'
        ),
      selectAllConversations: () =>
        set(
          (state) => ({
            selectedConversations: state.conversations.map(
              (conversation) => conversation._id
            ),
          }),
          false,
          'selectAllConversations'
        ),
      clearSelection: () =>
        set({ selectedConversations: [] }, false, 'clearSelection'),
      //primaryTag for selecting plan after setting that tag
      primaryTag: 'Closed Won',
      setPrimaryTag: (tag) => set({ primaryTag: tag }, false, 'setPrimaryTag'),
    }),
    { name: 'InboxStore' }
  )
);

export default useInboxStore;

/* 
tags -> list
owners -> list

selectedTag
selectedOwner
selectedSort
searchText
selectedConversationState


*/
